/**
 * Aggregation helpers for case chapters that visualize the evidence
 * corpus as a whole: term counts summed across every archived collection
 * (the chapter is about formats, not about which crawl they came from).
 */

export interface TermCount {
  term: string;
  pages: number;
}

export interface SetTermCounts {
  set: string;
  terms: TermCount[];
}

/** Sum page counts per term across collections. */
export function mergeTermCounts(perSet: SetTermCounts[]): TermCount[] {
  const totals = new Map<string, number>();
  for (const { terms } of perSet) {
    for (const { term, pages } of terms) {
      totals.set(term, (totals.get(term) || 0) + pages);
    }
  }
  return [...totals.entries()]
    .map(([term, pages]) => ({ term, pages }))
    .sort((a, b) => b.pages - a.pages || a.term.localeCompare(b.term));
}

/** Top N terms with pages > 0. */
export function topTerms(merged: TermCount[], n: number): TermCount[] {
  return merged.filter((t) => t.pages > 0).slice(0, n);
}

/** For one term, the collections that mention it, biggest first — the
 *  order the instance stream walks through them. */
export function setsForTerm(perSet: SetTermCounts[], term: string): {
  set: string;
  pages: number;
}[] {
  return perSet
    .map(({ set, terms }) => ({
      set,
      pages: terms.find((t) => t.term === term)?.pages || 0,
    }))
    .filter((s) => s.pages > 0)
    .sort((a, b) => b.pages - a.pages);
}
